import { useContext } from "react";
import { ConfigContext, UPSContext, ServiceContext } from "@app/provider";
import {
  CreatePaymentMethodInput,
  CreatePaymentMethodResponseData,
  PaymentRequestResponseData,
  ResponseData,
  UpdatePaymentPlanInput,
  UPSErrors,
  CreatePaymentPlanInput,
  ConfirmPaymentInput,
  ConfirmPaymentPlanInput,
  ConfirmPaymentPlanResponseData,
  GuestValidationInputData,
  GuestValidationResponseData,
  PaymentPlanRequestResponseData,
  CalculateInstallmentResponseData,
  CalculateInstallmentRequestData,
  LoginValidationInputData,
  LoginValidationResponseData,
} from "@app/ups/models";

enum UPSApiRequests {
  GET = "GET",
  POST = "POST",
}

type HeadersType = {
  Accept?: string;
  "Content-Type": string;
  Authorization?: string;
};

export type UseUPSApiControllerType = {
  UPS_BASE_API_URL: string;
  guestValidation: (
    input: GuestValidationInputData
  ) => Promise<ResponseData<GuestValidationResponseData>>;
  loginValidation: (
    input: LoginValidationInputData
  ) => Promise<ResponseData<LoginValidationResponseData>>;
  createPaymentMethod: (
    input: CreatePaymentMethodInput
  ) => Promise<ResponseData<CreatePaymentMethodResponseData>>;
  updatePaymentMethod: (input: UpdatePaymentPlanInput) => Promise<ResponseData>;
  getPaymentRequest: (
    paymentRequestId: string
  ) => Promise<ResponseData<PaymentRequestResponseData>>;
  createDirectPayment: (
    paymentRequestId: string
  ) => Promise<ResponseData<CreatePaymentMethodResponseData>>;
  createPaymentPlan: (
    paymentRequestId: string,
    input: CreatePaymentPlanInput
  ) => Promise<ResponseData<CreatePaymentMethodResponseData>>;
  confirmDirectPayment: (
    paymentRequestId: string,
    input: ConfirmPaymentInput
  ) => Promise<ResponseData>;
  confirmPaymentPlan: (
    paymentRequestId: string,
    input: ConfirmPaymentPlanInput
  ) => Promise<ResponseData<ConfirmPaymentPlanResponseData>>;
  getPaymentPlanRequest: (
    paymentPlanId: string
  ) => Promise<ResponseData<PaymentPlanRequestResponseData>>;
  calculateInstallment: (
    input: CalculateInstallmentRequestData
  ) => Promise<ResponseData<CalculateInstallmentResponseData[]>>;
  getReceiptRequest: (receiptId: string) => Promise<ArrayBuffer>;
};

const useUPSApiController = (): UseUPSApiControllerType => {
  const {
    config: {
      ups: { baseApiUrl: UPS_BASE_API_URL },
    },
  } = useContext(ConfigContext);

  const { upsAccessToken } = useContext(UPSContext);
  const { sessionService } = useContext(ServiceContext);

  const getHeaders = async () => {
    const headers: HeadersType = {
      "Content-Type": "application/json;charset=UTF-8",
    };
    if (upsAccessToken) {
      headers.Authorization = `Bearer ${upsAccessToken}`;
    }

    return headers;
  };

  const fetchWithHandling = async <T>(
    url: string,
    method: string,
    body?: string
  ): Promise<ResponseData<T>> => {
    const headers = await getHeaders();
    const options: RequestInit = {
      method,
      headers,
      body,
    };
    const response = await fetch(url, options);
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error as UPSErrors);
    }

    const responseBody = await response.json();
    return {
      message: responseBody.message,
      data: responseBody.data,
    };
  };

  const guestValidation = async (
    input: GuestValidationInputData
  ): Promise<ResponseData<GuestValidationResponseData>> => {
    const headers: HeadersType = {
      "Content-Type": "application/json;charset=UTF-8",
    };
    const requestOptions = {
      method: UPSApiRequests.POST,
      headers: headers,
      body: JSON.stringify(input),
    };
    const response = await fetch(
      `${UPS_BASE_API_URL}/ups/verify/guest`,
      requestOptions
    );
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error as UPSErrors);
    }

    const responseBody = await response.json();
    return {
      message: responseBody.message,
      data: responseBody.data,
    };
  };

  const loginValidation = async (
    input: LoginValidationInputData
  ): Promise<ResponseData<LoginValidationResponseData>> => {
    const uppToken = await sessionService.getToken();
    const uppAccessToken = uppToken?.accessToken ?? "";

    const headers: HeadersType = {
      "Content-Type": "application/json;charset=UTF-8",
      Authorization: `Bearer ${uppAccessToken}`,
    };
    const requestOptions = {
      method: UPSApiRequests.POST,
      headers: headers,
      body: JSON.stringify(input),
    };
    const response = await fetch(
      `${UPS_BASE_API_URL}/ups/verify/login`,
      requestOptions
    );
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error as UPSErrors);
    }

    const responseBody = await response.json();
    return {
      message: responseBody.message,
      data: responseBody.data,
    };
  };

  const createPaymentMethod = async (
    input: CreatePaymentMethodInput
  ): Promise<ResponseData<CreatePaymentMethodResponseData>> => {
    return await fetchWithHandling(
      `${UPS_BASE_API_URL}/ups/payment-method`,
      UPSApiRequests.POST,
      JSON.stringify(input)
    );
  };

  const updatePaymentMethod = async (
    input: UpdatePaymentPlanInput
  ): Promise<ResponseData> => {
    const { paymentPlanId, setupIntentId } = input;

    return await fetchWithHandling(
      `${UPS_BASE_API_URL}/ups/payment-plan/${paymentPlanId}/update-payment-method`,
      UPSApiRequests.POST,
      JSON.stringify({ setupIntentId })
    );
  };

  const getPaymentPlanRequest = async (
    paymentPlanId: string
  ): Promise<ResponseData<PaymentPlanRequestResponseData>> => {
    return await fetchWithHandling(
      `${UPS_BASE_API_URL}/ups/payment-plan/${paymentPlanId}`,
      UPSApiRequests.GET
    );
  };

  const getPaymentRequest = async (
    paymentRequestId: string
  ): Promise<ResponseData<PaymentRequestResponseData>> => {
    return await fetchWithHandling(
      `${UPS_BASE_API_URL}/ups/payment-request/${paymentRequestId}`,
      UPSApiRequests.GET
    );
  };

  const createDirectPayment = async (
    paymentRequestId: string
  ): Promise<ResponseData<CreatePaymentMethodResponseData>> => {
    return await fetchWithHandling(
      `${UPS_BASE_API_URL}/ups/payment-request/${paymentRequestId}/process/directpay`,
      UPSApiRequests.POST,
      JSON.stringify({})
    );
  };

  const createPaymentPlan = async (
    paymentRequestId: string,
    input: CreatePaymentPlanInput
  ): Promise<ResponseData<CreatePaymentMethodResponseData>> => {
    return await fetchWithHandling(
      `${UPS_BASE_API_URL}/ups/payment-request/${paymentRequestId}/process/paymentplan`,
      UPSApiRequests.POST,
      JSON.stringify(input)
    );
  };

  const confirmDirectPayment = async (
    paymentRequestId: string,
    input: ConfirmPaymentInput
  ): Promise<ResponseData> => {
    return await fetchWithHandling(
      `${UPS_BASE_API_URL}/ups/payment-request/${paymentRequestId}/confirm/directpay`,
      UPSApiRequests.POST,
      JSON.stringify(input)
    );
  };

  const confirmPaymentPlan = async (
    paymentRequestId: string,
    input: ConfirmPaymentPlanInput
  ): Promise<ResponseData<ConfirmPaymentPlanResponseData>> => {
    return await fetchWithHandling(
      `${UPS_BASE_API_URL}/ups/payment-request/${paymentRequestId}/confirm/paymentplan`,
      UPSApiRequests.POST,
      JSON.stringify(input)
    );
  };

  const calculateInstallment = async (
    input: CalculateInstallmentRequestData
  ): Promise<ResponseData<CalculateInstallmentResponseData[]>> => {
    return await fetchWithHandling(
      `${UPS_BASE_API_URL}/ups/payment-plan/calculate-installment`,
      UPSApiRequests.POST,
      JSON.stringify(input)
    );
  };

  const getReceiptRequest = async (receiptId: string): Promise<ArrayBuffer> => {
    const headers = await getHeaders();
    headers.Accept = "application/pdf";

    const response = await fetch(
      `${UPS_BASE_API_URL}/ups/payment/receipt/${receiptId}`,
      {
        method: UPSApiRequests.GET,
        headers,
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error as UPSErrors);
    }

    return response.arrayBuffer();
  };

  return {
    UPS_BASE_API_URL,
    guestValidation,
    loginValidation,
    createPaymentMethod,
    updatePaymentMethod,
    getPaymentRequest,
    createDirectPayment,
    createPaymentPlan,
    confirmDirectPayment,
    confirmPaymentPlan,
    getPaymentPlanRequest,
    calculateInstallment,
    getReceiptRequest,
  };
};

export default useUPSApiController;
